"use client";

import { captureError } from "@/lib/sentry-client";

export type ApiResult<T> = { ok: true; data: T } | { ok: false; error: string };

type ApiData = Record<string, unknown>;

async function parse<T extends ApiData>(res: Response): Promise<ApiResult<T>> {
  const data = (await res.json().catch(() => null)) as (T & { success?: boolean; error?: unknown }) | null;
  if (!res.ok || !data || data.success === false) {
    const message = typeof data?.error === "string" && data.error ? data.error : "Sunucu hatası. Tekrar deneyin.";
    return { ok: false, error: message };
  }
  return { ok: true, data: data as T };
}

/** GET — { success, ... } zarfını çözer, hatayı Türkçe mesaja indirger. */
export async function apiGet<T extends ApiData>(url: string, route: string): Promise<ApiResult<T>> {
  try {
    const res = await fetch(url, { cache: "no-store" });
    return await parse<T>(res);
  } catch (err) {
    captureError(err, { route });
    return { ok: false, error: "Bağlantı hatası. Tekrar deneyin." };
  }
}

async function send<T extends ApiData>(method: string, url: string, route: string, body?: unknown): Promise<ApiResult<T>> {
  try {
    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return await parse<T>(res);
  } catch (err) {
    captureError(err, { route });
    return { ok: false, error: "Bağlantı hatası. Tekrar deneyin." };
  }
}

export function apiPost<T extends ApiData>(url: string, route: string, body?: unknown): Promise<ApiResult<T>> {
  return send<T>("POST", url, route, body);
}

export function apiPut<T extends ApiData>(url: string, route: string, body?: unknown): Promise<ApiResult<T>> {
  return send<T>("PUT", url, route, body);
}

export function apiPatch<T extends ApiData>(url: string, route: string, body?: unknown): Promise<ApiResult<T>> {
  return send<T>("PATCH", url, route, body);
}

export function apiDelete<T extends ApiData>(url: string, route: string): Promise<ApiResult<T>> {
  return send<T>("DELETE", url, route);
}
